// Multi-price order ladder — pure helpers (no React, no app deps) for the
// Kalshi "place many limit orders in one click" flow in GameDetailPanel's
// OrderEntryForm. Parsing / validation / structural aggregation live here so
// they're unit-testable; the component owns the fee math (which needs the live
// Hydra fee schedule) and the paced submit loop.

/** One ladder rung as raw input strings (cents + whole contracts). */
export interface LadderLeg {
  id: string;
  /** Which team's market this rung is placed against. The YES/NO side is
   *  global to the whole ticket (never per-rung) so a market is never quoted
   *  YES and NO at once — only the team varies per rung. */
  team: 'away' | 'home';
  /** Price in cents, "1".."99". */
  price: string;
  /** Whole contracts, ">=1". */
  qty: string;
}

/** A validated, parsed rung. */
export interface ParsedLeg {
  priceCents: number;
  qty: number;
}

/** Max rungs a single ladder may hold — bounds a fat-finger burst. */
export const LADDER_MAX_LEGS = 20;

/** Minimum spacing between order submissions ⇒ ≤4 orders/sec, honoring the
 *  shared Kalshi per-account rate bucket (~4 req/s intended budget). */
export const MIN_ORDER_SPACING_MS = 250;

export type LegValidation =
  | { ok: true; parsed: ParsedLeg }
  | { ok: false; error: string };

/** Validate one rung against Kalshi limit-order rules: price a whole 1..99¢,
 *  qty a whole integer ≥ 1. Empty / non-numeric / fractional all reject. */
export function validateLeg(leg: { price: string; qty: string }): LegValidation {
  const priceCents = Number(leg.price);
  const qty = Number(leg.qty);
  // Number('') === 0 and Number('1.5') === 1.5, both caught by the guards below.
  if (leg.price.trim() === '' || !Number.isInteger(priceCents) || priceCents < 1 || priceCents > 99) {
    return { ok: false, error: 'Price must be a whole 1–99¢' };
  }
  if (leg.qty.trim() === '' || !Number.isInteger(qty) || qty < 1) {
    return { ok: false, error: 'Qty must be a whole number ≥ 1' };
  }
  return { ok: true, parsed: { priceCents, qty } };
}

export interface LadderBuild {
  /** Rungs that passed validation, in ladder order (leg 0 first). */
  valid: ParsedLeg[];
  /** Failures, indexed into the full [leg0, ...extraLegs] array (0 == leg 0). */
  invalid: { index: number; error: string }[];
}

/** Combine leg 0 (the base price/qty inputs) with the extra rungs and validate
 *  all of them. Index 0 is leg 0; index i>0 maps to extraLegs[i-1]. */
export function buildLadderLegs(
  leg0: { price: string; qty: string },
  extraLegs: readonly { price: string; qty: string }[],
): LadderBuild {
  const all = [leg0, ...extraLegs];
  const valid: ParsedLeg[] = [];
  const invalid: { index: number; error: string }[] = [];
  all.forEach((leg, index) => {
    const v = validateLeg(leg);
    if (v.ok) valid.push(v.parsed);
    else invalid.push({ index, error: v.error });
  });
  return { valid, invalid };
}

export interface LadderSummary {
  legs: number;
  contracts: number;
  /** Σ priceCents×qty — notional in cents, excludes fees. */
  notionalCents: number;
  /** Contracts-weighted average price in cents (0 when no contracts). */
  avgPriceCents: number;
  /** Each contract pays $1 on win ⇒ payout cents = contracts×100. */
  maxPayoutCents: number;
}

/** Fee-free structural aggregate of the valid rungs. The component layers the
 *  live fee estimate on top (fees need the Hydra fee schedule + MM rebate). */
export function summarizeLegs(valid: readonly ParsedLeg[]): LadderSummary {
  let contracts = 0;
  let notionalCents = 0;
  for (const leg of valid) {
    contracts += leg.qty;
    notionalCents += leg.priceCents * leg.qty;
  }
  return {
    legs: valid.length,
    contracts,
    notionalCents,
    avgPriceCents: contracts === 0 ? 0 : notionalCents / contracts,
    maxPayoutCents: contracts * 100,
  };
}
